import React, { useEffect, useState } from 'react';
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { SegInput } from '../../../Components/SegUI/SegInput/SegInputLanding';
import CodigosDeArea from '../../../Assets/CodigosDeArea.json';
import { SegButton } from '../../../Components/SegUI/SegButton/SegButton';
import { useCotizacionStore } from '../../../Services/Providers/CotizacionStore';
import guardarContactoGenerico from '../../../Services/API/GuardarContactoGenerico';
import { useConfig } from '../../../context/ConfigContext';
import { getRecaptchaKey } from '../../../Services/API/helper';
import { esLeadFallido } from '../../../Services/API/esLeadFallido';
import { ErrorEnvioLead } from '../../../Components/ErrorEnvioLead/ErrorEnvioLead';
import { getWhatsappUrl } from '../../../utils/whatsapp';
import { SegSelect, SegSelectOption } from '../../../Components/SegUI/SegSelect/SegSelect';
import obtenerProvincias from '../../../Services/API/Provincias';
import obtenerLocalidaXProvincia from '../../../Services/API/Localidades';

interface FormularioProps {
  titulo: string;
  rubro: string;
  wpp?: boolean;
}

type FormValues = {
  setNombre: string;
  setApellido: string;
  setPrefijo: string;
  setNumero: string;
  setEmail: string;
  setCodigoPostal: string;
  setLocalidad: string;
  setProvincia: string;
  setVivienda: string;
};

// ✅ Validación unificada para AR / UY
const validarTelefono = (isUY: boolean, prefijo: string, numero: string) => {
  if (isUY) {
    // En UY no pedimos prefijo en el formulario; el prefijo técnico es "9" y el número debe tener 8 dígitos
    return /^[0-9]{8}$/.test(numero || '');
  }
  const numeroLimpio = String(prefijo || '') + String(numero || '');
  if (!CodigosDeArea.includes(prefijo || '')) return false; // Prefijo válido en AR
  // En AR: prefijo (2–4 dígitos del catálogo) + número (6–9 dígitos) => total exactamente 10
  return /^[0-9]{6,9}$/.test(numero || '') && numeroLimpio.length === 10;
};

export const FormularioIndividuoConSelect = ({
  titulo,
  rubro,
  wpp = true,
}: FormularioProps) => {
  const { config } = useConfig();
  const isUY = config?.pais === 'UY';

  const [isLoading, setIsLoading] = useState(false);
  const [formularioEnviado, setFormularioEnviado] = React.useState<boolean>(false);
  const [isTelefonoValido, setIsTelefonoValido] = React.useState<boolean>(false);
  const [errorEnvio, setErrorEnvio] = useState<boolean>(false);

  const methods = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      setPrefijo: isUY ? '9' : '', // UY: prefijo técnico fijo
      setNumero: '',
    },
  });
  const { handleSubmit, control, setValue, getValues, formState, trigger, setError, clearErrors, watch } = methods;
  const { cotizacionValues, setCotizacionValues } = useCotizacionStore();

  const [localidadLoad, setLocalidadLoad] = useState<boolean>(false);
  const [provinciaLoad, setProvinciaLoad] = useState<boolean>(false);

  const [provincias, setProvincias] = useState<SegSelectOption[]>([]);
  const [localidades, setLocalidades] = useState<SegSelectOption[]>([]);
  const [viviendas] = useState<SegSelectOption[]>([
    { value: '1', label: 'Casa' },
    { value: '2', label: 'Departamento' },
  ]);

  const [provincia, setProvincia] = useState<SegSelectOption>();
  const [localidad, setLocalidad] = useState<SegSelectOption>();
  const [vivienda, setVivienda] = useState<SegSelectOption>();

  const fetchProvincias = async () => {
    setProvinciaLoad(true);
    const response: any = await obtenerProvincias();
    setProvinciaLoad(false);
    const formattedData = response.map((item: any) => ({ value: item, label: item }));
    setProvincias(formattedData);
  };

  const fetchLocalidades = async (provincia: SegSelectOption) => {
    if (provincia !== null && provincia !== undefined) {
      setLocalidadLoad(true);
      const response: any = await obtenerLocalidaXProvincia(provincia?.value);
      setLocalidadLoad(false);
      const formattedData = response.map((item: any) => ({ value: item, label: item }));
      setLocalidades(formattedData);
    }
  };

  useEffect(() => {
    fetchProvincias().then(() => {});
  }, []);

  // ✅ Revalidar teléfono en cada cambio relevante
  const validarYActualizarTelefono = () => {
    const { setPrefijo, setNumero } = getValues();
    setIsTelefonoValido(validarTelefono(isUY, setPrefijo, setNumero));
  };

  // ✅ Si cambia el país en runtime, ajustar prefijo y revalidar
  useEffect(() => {
    setValue('setPrefijo', isUY ? '9' : (getValues('setPrefijo') || ''));
    validarYActualizarTelefono();
  }, [isUY]);

  const onChangeProvicia = async (e: SegSelectOption) => {
    setProvincia(e);
    setLocalidad(undefined);
    await fetchLocalidades(e);
  };

  const obtenerTokenRecaptcha = (key: any): Promise<string> => {
    return new Promise((resolve) => {
      // @ts-ignore
      grecaptcha.ready(function () {
        // @ts-ignore
        grecaptcha.execute(key!, { action: 'submit' }).then(function (token: string) {
          setCotizacionValues({ ...cotizacionValues, captchaToken: token });
          resolve(token);
        });
      });
    });
  };

  const onSubmit: SubmitHandler<FormValues> = async(values: FormValues) => {
    setIsLoading(true);
    setErrorEnvio(false);

    const { setNombre, setApellido, setPrefijo, setNumero, setEmail, setCodigoPostal, setLocalidad, setProvincia, setVivienda } = values;

    const updatedValues = {
      ...cotizacionValues,
      nombre: setNombre,
      apellido: setApellido,
      telefono: {
        prefijo: isUY ? '9' : setPrefijo,
        numero: setNumero,
      },
      email: (setEmail || '').toLowerCase(),
      codigoPostal: setCodigoPostal,
      localidadLanding: setLocalidad,
      rubro: rubro,
      localidad: { value: setLocalidad, label: setLocalidad },
      provincia: { value: setProvincia, label: setProvincia },
      tipoVivienda: setVivienda,
    };

    const key = await getRecaptchaKey();

    obtenerTokenRecaptcha(key).then((token: string) => {
      if (!cotizacionValues?.contactoId || cotizacionValues.rubro !== rubro) {
        const fetchData = async () => {
          const response: any = await guardarContactoGenerico({ ...updatedValues, captchaToken: token }, 'UY-SEGURARSE', undefined, config?.pais);
          if (!esLeadFallido(response)) {
            if (response.data.api_status === 200 && response.data.api_data !== null) {
              setCotizacionValues({
                ...updatedValues,
                contactoId: response.data.api_data.idContacto,
                token: response.data.api_data.token,
                captchaToken: token,
              });
              setFormularioEnviado(true);
              setIsLoading(false);
            } else if (response.data.api_data === 'El Captcha no es válido') {
              setIsLoading(false);
              setErrorEnvio(true);
              console.log('Error en el captcha');
            } else {
              console.error('Error al guardar contacto');
              setIsLoading(false);
              setErrorEnvio(true);
            }
          } else {
            setIsLoading(false);
            setErrorEnvio(true);
          }
        };
        fetchData();
      } else {
        setCotizacionValues({ ...updatedValues });
        setFormularioEnviado(true);
        document.getElementById('formulario')?.scrollIntoView({ behavior: 'smooth' });
      }
    });
  };

  return (
    <>
      <div id="formulario"></div>
      <div>
        {!formularioEnviado && (
          <div className="py-40 py-lg-50 bg-f3">
            <div className="container px-18">
              <h2 className="ff-mukta fw-bold fs-30 fs-lg-40 text-black text-center mb-18 mb-lg-40">{titulo}</h2>
              <div className="bg-white rounded-18 shadow-4 px-30 px-lg-60 py-30 py-lg-40">
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="row gx-lg-5 align-items-end">
                    {/* Nombre */}
                    <div className="col-lg-6">
                      <div className="mb-20 mb-lg-35">
                        <div className="fs-20 fw-bold text-black mb-1">Nombre</div>
                        <Controller
                          name="setNombre"
                          control={control}
                          rules={{
                            validate: {
                              required: (value) => !!value || 'Ingresá el nombre',
                              pattern: (value) => /^[a-zA-ZáéíóúüñÁÉÍÓÚÜÑ\s]+$/.test(value || '') || 'Nombre inválido',
                            },
                          }}
                          render={({ field, fieldState }) => (
                            <SegInput
                              className="fs-16"
                              label=""
                              name="setNombre"
                              type="text"
                              width={100}
                              placeHolder="Ej: Juan"
                              onChange={(e) => {
                                field.onChange(e);
                                setValue('setNombre', e.target.value);
                                trigger('setNombre');
                              }}
                              value={field.value}
                              autoFocus={false}
                              leyendaError={fieldState?.error?.message}
                              borderColor={`${formState.errors.setNombre ? 'var(--bs-form-invalid-border-color)' : ''}`}
                              focusBorderColor={`${formState.errors.setNombre ? 'var(--bs-form-invalid-border-color)' : '#2c7dfd'}`}
                              absolut
                            />
                          )}
                        />
                      </div>
                    </div>

                    {/* Apellido */}
                    <div className="col-lg-6">
                      <div className="mb-20 mb-lg-35">
                        <div className="fs-20 fw-bold text-black mb-1">Apellido</div>
                        <Controller
                          name="setApellido"
                          control={control}
                          rules={{
                            validate: {
                              required: (value) => !!value || 'Ingresá el apellido',
                              pattern: (value) => /^[a-zA-ZáéíóúüñÁÉÍÓÚÜÑ\s]+$/.test(value || '') || 'Apellido inválido',
                            },
                          }}
                          render={({ field, fieldState }) => (
                            <SegInput
                              className="fs-16"
                              label=""
                              name="setApellido"
                              type="text"
                              width={100}
                              placeHolder="Ej: García"
                              onChange={(e) => {
                                field.onChange(e);
                                setValue('setApellido', e.target.value);
                                trigger('setApellido');
                              }}
                              value={field.value}
                              leyendaError={fieldState?.error?.message}
                              borderColor={`${formState.errors.setApellido ? 'var(--bs-form-invalid-border-color)' : ''}`}
                              focusBorderColor={`${formState.errors.setApellido ? 'var(--bs-form-invalid-border-color)' : '#2c7dfd'}`}
                              absolut
                            />
                          )}
                        />
                      </div>
                    </div>

                    {/* Teléfono celular */}
                    <div className="col-lg-6">
                      <div className="mb-20 mb-lg-35">
                        <div className="fs-20 fw-bold text-black mb-1">Teléfono celular</div>
                        <div className={`row g-2 ${isUY ? 'align-items-end' : ''}`}>
                          {/* Cód. área solo si NO es UY */}
                          {!isUY && (
                            <div className="col-4">
                              <div className="fs-16 text-6e mb-1">Cód. área</div>
                              <Controller
                                name="setPrefijo"
                                control={control}
                                rules={{
                                  validate: {
                                    required: (value) => !!value || 'Ingresá el Cód. Área',
                                    isInValidCodeArea: (value) => CodigosDeArea.includes(value || '') || 'Cód. Área inválido',
                                  },
                                }}
                                render={({ field, fieldState }) => (
                                  <SegInput
                                    isGroup={true}
                                    labelGroup="0"
                                    className="fs-16"
                                    label=""
                                    name="setPrefijo"
                                    type="number"
                                    width={100}
                                    placeHolder="11"
                                    value={field.value}
                                    onKeyDown={(event) => {
                                      if (event.key === 'e' || event.key === 'E' || event.key === '+' || event.key === '-') {
                                        event.preventDefault();
                                      }
                                    }}
                                    autoFocus={false}
                                    onChange={(e) => {
                                      field.onChange(e);
                                      const prefijo = e.target.value;
                                      setValue('setPrefijo', prefijo);
                                      trigger('setPrefijo');
                                      const numero = getValues('setNumero');
                                      if (numero !== '' && !validarTelefono(false, prefijo, numero)) {
                                        setError('setNumero', { type: 'validate', message: 'Número inválido' });
                                      } else {
                                        clearErrors('setNumero');
                                      }
                                      validarYActualizarTelefono();
                                    }}
                                    leyendaError={fieldState?.error?.message}
                                    borderColor={`${formState.errors.setPrefijo ? 'var(--bs-form-invalid-border-color)' : ''}`}
                                    focusBorderColor={`${formState.errors.setPrefijo ? 'var(--bs-form-invalid-border-color)' : '#2c7dfd'}`}
                                    absolut
                                  />
                                )}
                              />
                            </div>
                          )}

                          {/* Número */}
                          <div className={isUY ? 'col-12' : 'col-8'}>
                            {!isUY && <div className="fs-16 text-6e mb-1">Número de teléfono</div>}
                            <Controller
                              name="setNumero"
                              control={control}
                              rules={{
                                validate: {
                                  required: (value) => !!value || 'Ingresá Número de Celular',
                                  pattern: (value) => (isUY ? /^[0-9]{8}$/.test(value || '') : /^[0-9]{6,9}$/.test(value || '')) || 'Número inválido',
                                  validateCombination: (value) => {
                                    if (isUY) return true; // En UY no se combina con prefijo ingresado por el usuario
                                    const prefijo = watch('setPrefijo');
                                    return (String(prefijo || '') + String(value || '')).length === 10 || 'Número inválido';
                                  },
                                },
                              }}
                              render={({ field, fieldState }) => (
                                <SegInput
                                  isGroup={true}
                                  labelGroup={isUY ? '9' : '15'}
                                  className="fs-16"
                                  label={isUY ? '' : ''}
                                  name="setNumero"
                                  type={isUY ? 'tel' : 'number'}
                                  width={100}
                                  placeHolder={isUY ? '33669955' : '33669955'}
                                  value={field.value}
                                  onKeyDown={(event) => {
                                    if (!isUY && (event.key === 'e' || event.key === 'E' || event.key === '+' || event.key === '-')) {
                                      event.preventDefault();
                                    }
                                  }}
                                  autoFocus={false}
                                  onChange={(e) => {
                                    field.onChange(e);
                                    setValue('setNumero', e.target.value);
                                    trigger('setNumero');
                                    validarYActualizarTelefono();
                                  }}
                                  leyendaError={fieldState?.error?.message}
                                  borderColor={`${formState.errors.setNumero ? 'var(--bs-form-invalid-border-color)' : ''}`}
                                  focusBorderColor={`${formState.errors.setNumero ? 'var(--bs-form-invalid-border-color)' : '#2c7dfd'}`}
                                  absolut
                                />
                              )}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Email */}
                    <div className="col-lg-6">
                      <div className="mb-20 mb-lg-35">
                        <div className="fs-20 fw-bold text-black mb-1">Email</div>
                        <Controller
                          name="setEmail"
                          control={control}
                          rules={{
                            validate: {
                              required: (value) => !!value || 'Ingresá el email',
                              pattern: (value) => /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(value || '') || 'Email inválido',
                            },
                          }}
                          render={({ field, fieldState }) => (
                            <SegInput
                              className="fs-16"
                              label=""
                              name="setEmail"
                              type="text"
                              width={100}
                              placeHolder={isUY ? "Ej: nombre@dominio.com.uy" : "Ej: nombre@dominio.com.ar"}
                              onChange={(e) => {
                                field.onChange(e);
                                setValue('setEmail', e.target.value);
                                trigger('setEmail');
                              }}
                              value={field.value}
                              autoFocus={false}
                              leyendaError={fieldState?.error?.message}
                              borderColor={`${formState.errors.setEmail ? 'var(--bs-form-invalid-border-color)' : ''}`}
                              focusBorderColor={`${formState.errors.setEmail ? 'var(--bs-form-invalid-border-color)' : '#2c7dfd'}`}
                              absolut
                            />
                          )}
                        />
                      </div>
                    </div>

                    {/* Departamento */}
                    <div className="col-lg-6">
                      <div className="mb-20 mb-lg-35">
                        <div className="fs-20 fw-bold text-black" style={{ marginBottom: '-16px' }}>Departamento</div>
                        <FormProvider {...methods}>
                          <SegSelect
                            selected={provincia}
                            setSelected={onChangeProvicia}
                            name="setProvincia"
                            options={provincias}
                            width={'100%'}
                            height={'md'}
                            loading={provinciaLoad}
                            borderColor={formState.errors.setProvincia ? 'red' : '#dee2e6'}
                            openDefault={false}
                            hoverBorderColor='#dee2e6'
                            focusBorderColor={`${formState.errors.setProvincia ? 'var(--bs-form-invalid-border-color)' : '#2c7dfd'}`}
                          />
                        </FormProvider>
                        {<p>{(formState.errors as any).setProvincia?.message}</p>}
                      </div>
                    </div>

                    {/* Localidad */}
                    <div className="col-lg-6">
                      <div className="mb-20 mb-lg-35">
                        <div className="fs-20 fw-bold text-black" style={{ marginBottom: '-16px' }}>Localidad</div>
                        <FormProvider {...methods}>
                          <SegSelect
                            selected={localidad}
                            setSelected={setLocalidad}
                            name="setLocalidad"
                            options={localidades}
                            width={'100%'}
                            height={'md'}
                            loading={localidadLoad}
                            borderColor={formState.errors.setLocalidad ? 'red' : '#dee2e6'}
                            hoverBorderColor='#dee2e6'
                            openDefault={false}
                            focusBorderColor={`${formState.errors.setLocalidad ? 'var(--bs-form-invalid-border-color)' : '#2c7dfd'}`}
                          />
                        </FormProvider>
                        {<p>{(formState.errors as any).setLocalidad?.message}</p>}
                      </div>
                    </div>

                    {/* Tipo vivienda */}
                    <div className="col-lg-6">
                      <div className="mb-20 mb-lg-35">
                        <div className="fs-20 fw-bold text-black" style={{ marginBottom: '-16px' }}>Tipo de Vivienda</div>
                        <FormProvider {...methods}>
                          <SegSelect
                            selected={vivienda}
                            setSelected={setVivienda}
                            name="setVivienda"
                            options={viviendas}
                            width={'100%'}
                            height={'md'}
                            loading={false}
                            borderColor={formState.errors.setVivienda ? 'red' : '#dee2e6'}
                            hoverBorderColor='#dee2e6'
                            openDefault={false}
                            focusBorderColor={`${formState.errors.setVivienda ? 'var(--bs-form-invalid-border-color)' : '#2c7dfd'}`}
                          />
                        </FormProvider>
                        {<p>{(formState.errors as any).setVivienda?.message}</p>}
                      </div>
                    </div>

                    {/* Cotizar button */}
                    <div className="text-center pt-3">
                      <SegButton
                        label="Cotizar"
                        type="submit"
                        className="btn btn-primary fs-20 fs-lg-25 fw-bold w-200p w-lg-250p"
                        disabled={!formState.isValid || !provincia || !localidad || !vivienda}
                        loading={isLoading}
                        spinnerSize="small"
                      />
                      {errorEnvio && <ErrorEnvioLead whatsappHref={getWhatsappUrl(config?.pais)} />}
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
        {formularioEnviado && (
          <div className="py-40 py-lg-50 bg-f3">
            <div className="container px-18">
              <h2 className="ff-mukta fw-bold fs-30 fs-lg-40 text-primary text-center mb-7 mb-lg-12">¡Recibimos tu solicitud!</h2>
              <div className="col-10 col-lg-12 mx-auto">
                <p className="fs-16 fs-lg-20 fw-medium lh-13 text-black text-center mb-25 mb-lg-30">
                  En breve nos comunicaremos con vos para asesorarte en la mejor cobertura.
                </p>
              </div>
              <div className="bg-white rounded-18 shadow-4 px-30 px-lg-60 py-30 py-lg-40">
                <dl className="row fs-16 fs-lg-18 mb-0">
                  <div className="col-md-6">
                    <dt className="fw-normal">
                      <svg className="svg-icon text-primary me-10" width="27" height="27">
                        <use xlinkHref="#tel-lin"></use>
                      </svg>
                      Teléfono:
                    </dt>
                    <dd className="fw-bold lh-12 ps-40 mb-20 mb-lg-30">
                      {cotizacionValues.telefono?.prefijo && cotizacionValues.telefono?.numero
                        ? cotizacionValues.telefono?.prefijo + cotizacionValues.telefono?.numero
                        : ''}
                    </dd>
                  </div>
                  <div className="col-md-6">
                    <dt className="fw-normal">
                      <svg className="svg-icon text-primary me-10" width="27" height="27">
                        <use xlinkHref="#envelope-lin"></use>
                      </svg>
                      Email:
                    </dt>
                    <dd className="fw-bold lh-12 ps-40 mb-20 mb-lg-30">{cotizacionValues.email}</dd>
                  </div>
                  <div className="col-md-6">
                    <dt className="fw-normal">
                      <svg className="svg-icon text-primary me-10" width="27" height="27">
                        <use xlinkHref="#location-lin"></use>
                      </svg>
                      Localidad:
                    </dt>
                    <dd className="fw-bold lh-12 ps-40 mb-20 mb-lg-30">{cotizacionValues.localidadLanding}</dd>
                  </div>
                </dl>

                {(wpp && false) && (
                  <p className="fs-17 fw-medium text-center mb-0">
                    ¿Querés continuar por WhatsApp?
                    <span> </span>
                    <a
                      href="https://api.whatsapp.com/send/?phone=5491128357048&text&type=phone_number&app_absent=0"
                      target="_blank"
                      rel="noreferrer"
                      className="d-inline-flex align-items-center fs-17 text-black"
                    >
                      <span>Chatéanos</span>
                      <svg className="svg-icon ms-2 w-12em">
                        <use xlinkHref="#whatsapp"></use>
                      </svg>
                    </a>
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}